import { useMemo } from 'react';
import { useSettings } from '@wu/widget-core';
import type { FiltersSorting, Job, SalaryValue } from '../../../types';
import { useVisibleJobs } from '../../utils';
import { useJobFilters } from '../components';
import { buildSalaryBuckets } from './buildSalaryBuckets';
import { useFormatSalaryRange } from './useFormatSalaryRange';

export function usePrepareFilterOptions() {
  const { filtersSorting, language } = useSettings();
  const { selectedCurrency, selectedPeriod } = useJobFilters();
  const jobs = useVisibleJobs();

  const prepareSalaryOptions = usePrepareSalaryOptions();

  return useMemo(
    () => ({
      company: prepareFilterOptions({
        jobs,
        field: 'company',
        sorting: filtersSorting
      }),
      department: prepareFilterOptions({
        jobs,
        field: 'department',
        sorting: filtersSorting
      }),
      location: prepareFilterOptions({
        jobs,
        field: 'location',
        sorting: filtersSorting
      }),
      typeOfContract: prepareFilterOptions({
        jobs,
        field: 'typeOfContract',
        sorting: filtersSorting
      }),
      salary: prepareSalaryOptions(jobs, filtersSorting),
      skills: prepareSkillsFilterOptions(jobs, filtersSorting)
    }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [
      jobs,
      filtersSorting,
      selectedCurrency,
      selectedPeriod,
      language,
      prepareSalaryOptions
    ]
  );
}

type PrepareFilterOptionsParams = {
  jobs: Job[];
  field: keyof Pick<
    Job,
    'company' | 'department' | 'location' | 'typeOfContract'
  >;
  sorting: FiltersSorting;
};

function prepareFilterOptions({
  jobs,
  field,
  sorting
}: PrepareFilterOptionsParams) {
  const values = jobs.map((job) => job[field]).filter(Boolean);
  return deduplicateAndSortOptions(values, sorting).map((value) => ({
    value,
    label: value
  }));
}

function prepareSkillsFilterOptions(jobs: Job[], sorting: FiltersSorting) {
  const values = jobs.flatMap(({ skills }) => skills.map(({ name }) => name));
  return deduplicateAndSortOptions(values, sorting).map((value) => ({
    value,
    label: value
  }));
}

function usePrepareSalaryOptions() {
  const { selectedCurrency, selectedPeriod } = useJobFilters();

  const formatSalaryRange = useFormatSalaryRange();

  return (jobs: Job[], sorting: FiltersSorting) => {
    const { customOptions, amountOptions } = jobs.reduce<{
      customOptions: string[];
      amountOptions: SalaryValue[];
    }>(
      (acc, job) => {
        const { salary } = job;

        switch (salary.type) {
          case 'none':
            return acc;
          case 'custom':
            if (salary.customSalary.trim()) {
              acc.customOptions.push(salary.customSalary);
            }
            return acc;
          default:
            const hasSelectedCurrencyAndPeriod =
              salary.currency === selectedCurrency &&
              salary.period === selectedPeriod;

            if (hasSelectedCurrencyAndPeriod) {
              acc.amountOptions.push(salary);
            }
            return acc;
        }
      },
      { customOptions: [], amountOptions: [] }
    );

    const buckets = buildSalaryBuckets(amountOptions, selectedPeriod);

    const preparedOptions = buckets.map((bucket) => ({
      value: {
        type: 'range' as const,
        value: {
          from: bucket.low ?? 0,
          to: bucket.high ?? Infinity,
          currency: selectedCurrency,
          period: selectedPeriod
        }
      },
      label: formatSalaryRange(bucket, selectedCurrency)
    }));

    const sortedCustomOptions = deduplicateAndSortOptions(
      customOptions,
      sorting
    );

    const preparedCustomOptions = sortedCustomOptions.map((option) => ({
      value: { type: 'custom' as const, value: option },
      label: option
    }));

    return [...preparedOptions, ...preparedCustomOptions];
  };
}

function deduplicateAndSortOptions(values: string[], sorting: FiltersSorting) {
  const collator = new Intl.Collator(undefined, { caseFirst: 'upper' });
  const groups = new Map<string, { value: string; count: number }>();

  values.forEach((value) => {
    if (!value) {
      return;
    }

    const key = value.toLowerCase();
    const entry = groups.get(key);
    if (entry) {
      entry.count++;
      // если текущая строка "лучше" (с заглавной буквы), запоминаем её
      if (collator.compare(value, entry.value) < 0) {
        entry.value = value;
      }
    } else {
      groups.set(key, { value, count: 1 });
    }
  });

  const options = Array.from(groups.values());

  if (sorting === 'alphabetical') {
    options.sort((a, b) => collator.compare(a.value, b.value));
  } else {
    options.sort(
      (a, b) => b.count - a.count || collator.compare(a.value, b.value)
    );
  }

  return options.map(({ value }) => value);
}
